import { useEffect, useState } from 'react';
import type { Filters } from '../lib/filterQuery';
import { buildQueryString, emptyFilters } from '../lib/filterQuery';
import { getAccounts } from '../lib/api';
import { useCatalogStore } from '../store/catalogStore';
import CollabChip from './CollabChip';
import { hapticImpact, hapticSelection } from '../lib/telegram';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-mlbb-border py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-sm font-medium text-white"
      >
        {title}
        <span className={`transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
      </button>
      {open && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder: string;
}) {
  return (
    <input
      type="number"
      inputMode="numeric"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      placeholder={placeholder}
      className="w-full rounded-lg border border-mlbb-border bg-mlbb-bg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-mlbb-accent-from"
    />
  );
}

export default function FilterSheet({
  open,
  initial,
  onClose,
  onApply,
}: {
  open: boolean;
  initial: Filters;
  onClose: () => void;
  onApply: (filters: Filters) => void;
}) {
  const [draft, setDraft] = useState<Filters>(initial);
  const [count, setCount] = useState<number | null>(null);
  const { meta, skins, load } = useCatalogStore();

  useEffect(() => {
    if (open) {
      setDraft(initial);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const res = await getAccounts(buildQueryString(draft, { limit: 1 }));
        setCount(res.total ?? null);
      } catch {
        setCount(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [draft, open]);

  if (!open) return null;

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const toggleCollab = (id: string) => {
    hapticSelection();
    setDraft((d) => ({
      ...d,
      collabs: d.collabs.includes(id) ? d.collabs.filter((c) => c !== id) : [...d.collabs, id],
    }));
  };

  const toggleCollectorTier = (id: string) => {
    hapticSelection();
    setDraft((d) => ({
      ...d,
      collectorTiers: d.collectorTiers.includes(id)
        ? d.collectorTiers.filter((c) => c !== id)
        : [...d.collectorTiers, id],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl border border-mlbb-border bg-mlbb-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-mlbb-border px-4 py-3">
          <div className="text-base font-semibold text-white">Фильтры</div>
          <button type="button" onClick={onClose} className="text-white/50">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          <Section title="Цена">
            <div className="flex gap-2">
              <NumberInput value={draft.priceMin} onChange={(v) => set('priceMin', v)} placeholder="от" />
              <NumberInput value={draft.priceMax} onChange={(v) => set('priceMax', v)} placeholder="до" />
            </div>
            <div className="flex gap-2">
              {['USD', 'RUB', 'UZS'].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('currency', draft.currency === c ? undefined : c)}
                  className={`rounded-lg px-3 py-1.5 text-xs ${
                    draft.currency === c ? 'bg-mlbb-accent text-white' : 'bg-mlbb-bg text-white/60'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Коллаборации">
            <div className="flex flex-wrap gap-2">
              {(meta?.collaborations ?? []).filter((c) => c.totalSkins > 0).map((c) => (
                <CollabChip
                  key={c.id}
                  label={c.name}
                  icon={c.logo}
                  selected={draft.collabs.includes(c.id)}
                  onClick={() => toggleCollab(c.id)}
                />
              ))}
            </div>
          </Section>

          <Section title="Конкретные скины">
            <SkinSearchPicker
              skins={skins}
              selected={draft.skins}
              onChange={(keys) => set('skins', keys)}
            />
          </Section>

          <Section title="Тиры скинов">
            <div className="grid grid-cols-3 gap-2">
              <NumberInput value={draft.legendMin} onChange={(v) => set('legendMin', v)} placeholder="👑 Legend от" />
              <NumberInput value={draft.grandMin} onChange={(v) => set('grandMin', v)} placeholder="⭐ Grand от" />
              <NumberInput
                value={draft.collectorMin}
                onChange={(v) => set('collectorMin', v)}
                placeholder="💠 Collector от"
              />
            </div>
          </Section>

          <Section title="Коллекция">
            <div className="flex flex-wrap gap-1.5">
              {(meta?.collectorTiers ?? []).map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  onClick={() => toggleCollectorTier(tier.id)}
                  className={`flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2.5 text-xs ${
                    draft.collectorTiers.includes(tier.id) ? 'bg-mlbb-accent text-white' : 'bg-mlbb-bg text-white/60'
                  }`}
                >
                  <img src={tier.icon} alt="" className="h-5 w-5 rounded-full object-cover" />
                  {tier.name}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Валюты">
            <NumberInput value={draft.diamondsMin} onChange={(v) => set('diamondsMin', v)} placeholder="💎 Алмазы от" />
            <div>
              <div className="mb-1 text-xs text-white/50">🎡 Magic Wheel от {draft.wheelMin ?? 0}/200</div>
              <input
                type="range"
                min={0}
                max={200}
                value={draft.wheelMin ?? 0}
                onChange={(e) => set('wheelMin', Number(e.target.value) || undefined)}
                className="w-full"
              />
            </div>
          </Section>

        </div>

        <div className="flex gap-3 border-t border-mlbb-border p-4">
          <button
            type="button"
            onClick={() => {
              hapticImpact('light');
              setDraft(emptyFilters);
            }}
            className="flex-1 rounded-full border border-mlbb-border py-3 text-sm text-white/80"
          >
            Сбросить
          </button>
          <button
            type="button"
            onClick={() => {
              hapticImpact('medium');
              onApply(draft);
            }}
            className="flex-[2] rounded-full bg-mlbb-accent py-3 text-sm font-medium text-white"
          >
            Показать {count !== null ? count : '…'} аккаунтов
          </button>
        </div>
      </div>
    </div>
  );
}

function SkinSearchPicker({
  skins,
  selected,
  onChange,
}: {
  skins: { key: string; hero: string; name: string }[];
  selected: string[];
  onChange: (keys: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const matches =
    query.trim().length > 0
      ? skins.filter((s) => `${s.hero} ${s.name}`.toLowerCase().includes(query.toLowerCase())).slice(0, 12)
      : [];

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Найти скин..."
        className="w-full rounded-lg border border-mlbb-border bg-mlbb-bg px-3 py-2 text-sm text-white placeholder:text-white/30"
      />
      {matches.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {matches.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() =>
                onChange(selected.includes(s.key) ? selected.filter((k) => k !== s.key) : [...selected, s.key])
              }
              className={`rounded-full px-2.5 py-1 text-xs ${
                selected.includes(s.key) ? 'bg-mlbb-accent text-white' : 'bg-mlbb-bg text-white/60'
              }`}
            >
              {s.hero} – {s.name}
            </button>
          ))}
        </div>
      )}
      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((key) => {
            const skin = skins.find((s) => s.key === key);
            return (
              <span key={key} className="flex items-center gap-1 rounded-full bg-mlbb-accent px-2.5 py-1 text-xs text-white">
                {skin ? `${skin.hero} – ${skin.name}` : key}
                <button type="button" onClick={() => onChange(selected.filter((k) => k !== key))}>
                  ✕
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
